import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Pressable, Radio, styles, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { getCaption, getComponent, getImage } from './helpers';
import { DATE_FORMAT, METHOD, OPENPAY_CARDS, PROVIDER, URL_IMAGES } from './Payment.constants';
import { L10N } from './Payment.l10n';
import * as style from './Payment.module.css';
import { getSearchProps } from '../../../helpers/getSearchProps';
import { getSafeTrust } from '../../../trust';
import { EVENT, METRICS_EVENTS } from '../../helpers';

const Payment = ({
  children,
  config = {},
  currency,
  error = {},
  info = {},
  methods: propMethods = {},
  showErrors = false,
  onChange = () => {},
  onError = () => {},
  ...others
}) => {
  const { isDesktop } = useDevice();
  const { currencyFormat, dateFormat, translate } = useLocale();
  const { set, value: store = {} } = useStore();

  const methods = Array.isArray(propMethods) ? propMethods : Object.keys(propMethods);

  const [cardType, setCardType] = useState();
  const [method, setMethod] = useState(methods.length > 0 ? methods[0] : undefined);

  useEffect(() => {
    onChange({ method });
    set({ payment: { method } });
    onError({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  const handleChange = (values = {}) => {
    const { type } = values;

    type !== cardType && setCardType(type);
    const next = { method, ...values };
    onChange(next);
    set({ payment: { ...next } });
  };

  const handleChangeMethod = (nextMethod) => {
    if (nextMethod !== method)
      getSafeTrust(store).record('journeyAction', { stage: 'checkout', name: 'paymentChanged' });
    setMethod(nextMethod);
    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.CHECKOUT_INTERACTION,
      action: 'method_selected',
      payment_method: nextMethod,
      ...getSearchProps({ store }),
    });
  };

  const { testId = 'payments' } = others;
  const standalone = methods.length === 1;
  const availableMethods = methods.filter((item) => !!METHOD[item]);

  return (
    <View {...others} role="payments" tag="payment" row={false} className={styles(style.container, others.className)}>
      {availableMethods.length > 1 && (
        <View className={style.item}>
          <Text bold headline className={style.header}>
            {translate(L10N.LABEL_PAYMENT_METHODS)}
          </Text>
        </View>
      )}
      {availableMethods.map((item) => {
        const selected = method === item;
        const methodConfig = { ...config, ...propMethods[item]?.config, provider: propMethods[item]?.config?.id };
        const caption = selected ? getCaption({ method, ...info, ...methodConfig }) : undefined;
        const component = selected
          ? getComponent({ method: item, methodConfig: propMethods[item], ...info, ...methodConfig })
          : undefined;
        const image = getImage({ method: item, ...methodConfig });
        const cards = methodConfig.cards || (methodConfig.provider === PROVIDER.OPENPAY ? OPENPAY_CARDS : []);
        const { time = {} } = info || {};

        return (
          <View key={item} className={style.item}>
            <Pressable
              disabled={selected || standalone}
              onPress={() => handleChangeMethod(item)}
              className={styles(style.header, selected && style.selected)}
              testId={testId ? `${testId}-item-${item}` : undefined}
            >
              {!standalone && (
                <Radio
                  checked={selected}
                  name="method"
                  value={item}
                  className={style.radio}
                  aria-label={translate(L10N[`LABEL_${item}`])}
                  aria-labelledby={item}
                />
              )}
              <Text
                bold
                headline={isDesktop && standalone}
                id={item}
                className={styles(style.title, selected && style.selected)}
                testId={testId ? `${testId}-text-${item}` : undefined}
              >
                {translate(L10N[`LABEL_${item}`])}
              </Text>

              {image && (
                <View
                  alt={`${item?.split('_').join(' ')}`}
                  src={`${process.env.SERVICE_STATIC}/${URL_IMAGES}/${image}`}
                  tag="img"
                  className={style.image}
                />
              )}

              {(([METHOD.CARD, METHOD.CARD_PREPAY, METHOD.PCIPROXY].includes(item) &&
                methodConfig.provider !== PROVIDER.LINKAGEPAY) ||
                (item === METHOD.TPV && methodConfig.provider === PROVIDER.OPENPAY)) &&
                cards.map((card) => (
                  <View
                    alt={card}
                    key={card}
                    src={`${process.env.SERVICE_STATIC}/${URL_IMAGES}/cards/${card.toLowerCase()}.png`}
                    tag="img"
                    className={styles(
                      style.image,
                      style.card,
                      selected && cardType && card !== cardType && style.disabled,
                    )}
                  />
                ))}
            </Pressable>

            {selected && (
              <View className={style.content} testId={testId ? `${testId}-container-${item}` : undefined}>
                {caption && (
                  <Text small className={styles(style.caption, component && style.withComponent)}>
                    {translate(caption, {
                      amount: currencyFormat({ currency, value: info?.amount }),
                      date: dateFormat(info?.date, DATE_FORMAT),
                      day: translate(L10N.LABEL_DAY, { value: time.value }),
                      hour: translate(L10N.LABEL_HOUR, { value: time.value }),
                      type: time.type,
                    })}
                  </Text>
                )}

                {component &&
                  React.createElement(component, {
                    error,
                    showErrors,
                    ...others,
                    ...methodConfig,
                    ...propMethods[item]?.prepayment,
                    onChange: handleChange,
                    onError,
                  })}
              </View>
            )}
          </View>
        );
      })}

      {children && <View className={style.children}>{children}</View>}
    </View>
  );
};

Payment.displayName = 'Mirai:Core:Payment';

Payment.propTypes = {
  children: PropTypes.any,
  config: PropTypes.shape({
    cards: PropTypes.arrayOf(PropTypes.string),
    showCVV: PropTypes.bool,
  }),
  currency: PropTypes.string,
  error: PropTypes.object,
  info: PropTypes.shape({
    cardValidation: PropTypes.bool,
    date: PropTypes.instanceOf(Date),
    amount: PropTypes.number,
    time: PropTypes.shape({
      type: PropTypes.number,
      value: PropTypes.number,
    }),
  }),
  methods: PropTypes.objectOf(
    PropTypes.shape({
      component: PropTypes.number,
      modal: PropTypes.number,
      config: PropTypes.shape({
        id: PropTypes.oneOf(Object.values(PROVIDER)),
        configurationId: PropTypes.number,
        publicKey: PropTypes.string,
      }),
      prepayment: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
      }),
    }),
  ).isRequired,
  showErrors: PropTypes.bool,
  onChange: PropTypes.func,
  onError: PropTypes.func,
};

export { Payment };
